import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, PackagePlus, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import MonerisCheckoutModal from '@/components/document-services/MonerisCheckoutModal';
import OrderSummary from '@/components/document-services/OrderSummary';
import ServiceFormRenderer from '@/components/document-services/ServiceFormRenderer';
import { ServiceError, ServiceLoading, ServicePageHeader } from '@/components/document-services/DocumentServicesCommon';
import {
  createMyShippingUpgrade,
  cancelMyServicePayment,
  getMyShippingUpgrade,
  startMyServiceCheckout,
  verifyMyServicePayment,
} from '@/lib/api';
import {
  apiErrorMessage,
  apiItems,
  orderId,
  productFormFields,
  productVariants,
  serviceAnswersWithDefaults,
  unwrapEntity,
  validateServiceAnswers,
  variantId,
  variantLabel,
} from '@/lib/documentServices';

export default function ServiceShippingUpgrade() {
  const { orderId: parentOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const parentItemId = searchParams.get('parent_item_id') || '';
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [createdOrder, setCreatedOrder] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await getMyShippingUpgrade(parentOrderId, parentItemId ? { parent_item_id: parentItemId } : {});
      const next = response.data || {};
      setData(next);
      const products = apiItems(next, ['products']);
      const fallbackProducts = next.product || next.shipping_product ? [next.product || next.shipping_product] : [];
      const choices = (products.length ? products : fallbackProducts)
        .flatMap((product) => productVariants(product).map((variant) => ({ product, variant })));
      setSelectedId((current) => choices.some((choice) => variantId(choice.variant) === current)
        ? current : variantId(choices[0]?.variant));
    } catch (err) {
      setError(apiErrorMessage(err, 'This order is not eligible for a shipping upgrade.'));
    } finally { setLoading(false); }
  }, [parentItemId, parentOrderId]);
  useEffect(() => { load(); }, [load]);

  const products = useMemo(() => {
    const listed = apiItems(data, ['products']);
    if (listed.length) return listed;
    const fallback = data?.product || data?.shipping_product;
    return fallback ? [fallback] : [];
  }, [data]);
  const parent = data?.parent_order || data?.order || {};
  const choices = useMemo(() => products.flatMap((item) => productVariants(item).map((variant) => ({ product: item, variant }))), [products]);
  const choice = choices.find((item) => variantId(item.variant) === selectedId) || choices[0];
  const product = choice?.product;
  const variant = choice?.variant;
  const fields = useMemo(() => productFormFields(product), [product]);
  const eligible = data?.eligible === true && choices.length > 0;

  const create = async () => {
    if (!eligible || !variant || !product || submittingRef.current) return;
    const submittedAnswers = serviceAnswersWithDefaults(fields, answers, variant);
    if (submittedAnswers !== answers) setAnswers(submittedAnswers);
    const localErrors = validateServiceAnswers(fields, submittedAnswers, variant);
    setErrors(localErrors);
    if (Object.keys(localErrors).length) return;
    submittingRef.current = true; setSubmitting(true); setError('');
    try {
      const acknowledgements = Object.fromEntries(fields.filter((field) => (
        ['checkbox', 'boolean', 'acknowledgement'].includes(String(field?.type || '').toLowerCase())
      )).map((field) => [field.key || field.id, submittedAnswers[field.key || field.id] === true]));
      const response = await createMyShippingUpgrade(parentOrderId, {
        product_id: product.id || product.uuid,
        variant_id: variantId(variant),
        parent_item_id: data?.parent_item_id || parentItemId,
        form_answers: submittedAnswers,
        acknowledgements,
      });
      const order = unwrapEntity(response.data, ['order', 'upgrade_order']);
      if (!orderId(order)) throw new Error('The upgrade order was created without an identifier.');
      setCreatedOrder(order); setPaymentOpen(false);
    } catch (err) {
      const fieldErrors = err?.response?.data?.detail?.field_errors || err?.response?.data?.field_errors;
      if (fieldErrors && typeof fieldErrors === 'object') setErrors(fieldErrors);
      setError(apiErrorMessage(err, err?.message || 'The shipping upgrade could not be created.'));
    }
    finally { submittingRef.current = false; setSubmitting(false); }
  };

  if (loading) return <ServiceLoading label="Checking shipping eligibility…" />;
  if (!data || (error && !products.length)) return <ServiceError message={error || 'Shipping upgrade is unavailable.'} onRetry={load} />;
  const createdId = orderId(createdOrder);
  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader backTo={`/student/orders/${encodeURIComponent(parentOrderId)}`} backLabel="Parent order" eyebrow="Shipping add-on" title="Add tracking to this order" description="Choose a tracked delivery upgrade. The recipient and address are inherited from the parent document order and cannot be changed here." />
      {!eligible && <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{data.reason || data.eligibility_reason || 'This order can no longer be upgraded.'}</div>}
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_370px]">
        <div className="space-y-5">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardContent className="p-5"><div className="flex items-start gap-3"><PackagePlus className="mt-0.5 size-5 text-[#D4AF37]" /><div><p className="text-xs font-semibold uppercase tracking-wide text-[#062B1A]/45">Parent order</p><p className="mt-1 font-semibold text-[#062B1A]">{parent.order_number || data.parent_order_number || parentOrderId}</p><p className="mt-1 text-sm text-[#062B1A]/55">{parent.document_type || parent.product_name || data.document_type || 'Document order'}</p></div></div></CardContent></Card>
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="text-lg text-[#062B1A]">Tracked delivery options</CardTitle></CardHeader><CardContent className="grid gap-3 sm:grid-cols-2">{choices.map((item) => { const id = variantId(item.variant); return <button key={id} type="button" disabled={Boolean(createdOrder) || !eligible} aria-pressed={id === variantId(variant)} onClick={() => { if (item.product.id !== product?.id) { setAnswers({}); setErrors({}); } setSelectedId(id); }} className={`rounded-xl border p-4 text-left ${id === variantId(variant) ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#062B1A]/10 bg-white'}`}><span className="font-semibold text-[#062B1A]">{item.product.name} — {variantLabel(item.variant)}</span><span className="mt-1 block text-xs text-[#062B1A]/50">Recipient and address stay unchanged</span></button>; })}</CardContent></Card>
          {fields.length > 0 && <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="text-lg text-[#062B1A]">Required confirmations</CardTitle></CardHeader><CardContent><ServiceFormRenderer fields={fields} answers={answers} onChange={setAnswers} errors={errors} variant={variant} disabled={Boolean(createdOrder)} /></CardContent></Card>}
          <div className="rounded-xl border border-[#062B1A]/10 bg-white p-4 text-xs leading-relaxed text-[#062B1A]/55"><ShieldCheck className="mr-2 inline size-4 text-[#D4AF37]" />Eligibility and price are rechecked by MCC when the add-on order is created. Duplicate upgrades and shipped orders are blocked by the server.</div>
        </div>
        <aside className="space-y-3 lg:sticky lg:top-6 lg:self-start"><OrderSummary {...(createdOrder ? { order: createdOrder } : { product, variant })} />{!createdOrder ? <Button type="button" onClick={create} disabled={!eligible || !variant || submitting} className="w-full bg-[#062B1A] text-white">{submitting && <Loader2 className="mr-2 size-4 animate-spin" />}Create upgrade order</Button> : <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"><p className="font-semibold">Upgrade order saved</p><p className="mt-1 text-xs">Review the server-confirmed total above before opening payment.</p><Button type="button" size="sm" onClick={() => setPaymentOpen(true)} className="mt-3 bg-[#062B1A] text-white">Continue payment</Button></div>}<Button asChild variant="ghost" className="w-full text-[#062B1A]"><Link to={`/student/orders/${encodeURIComponent(parentOrderId)}`}>Cancel and return</Link></Button></aside>
      </div>
      {createdOrder && <MonerisCheckoutModal open={paymentOpen} onOpenChange={setPaymentOpen} orderId={createdId} idempotencyKey={createdOrder.checkout_idempotency_key || createdOrder.payment_attempt?.idempotency_key || ''} startCheckout={startMyServiceCheckout} verifyPayment={verifyMyServicePayment} cancelPayment={cancelMyServicePayment} onVerified={(result) => navigate(`/student/orders/${encodeURIComponent(createdId)}/payment-result`, { state: { paymentResult: result } })} />}
    </div>
  );
}
